import {Component, OnInit, OnChanges, ViewChild, Input, Output, EventEmitter} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Company} from '../company';
import {CompanyService} from '../company.service';
import {MiscService} from '../../../shared/misc.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import { ErrorBoxService } from '../../../shared/error-box/error-box.service';
import { CityService } from '../../../shared/city.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/switchMap';
import * as _ from 'lodash';

@Component({
  selector: 'app-edit-company',
  templateUrl: './edit-company.component.html',
  styleUrls: ['./edit-company.component.scss']
})
export class EditCompanyComponent implements OnInit, OnChanges {

  @Input() isOffcanvasOpen: boolean = false;
  @Input() companyToEdit: Company = null;
  @Output() offcanvasClose = new EventEmitter<void>();
  @Output() companyUpdated = new EventEmitter<any>();

  company: Company;
  notFound = false;
  updatingCompany: boolean = false;
  isEditMode: boolean = false; // Toggle para activar edición

  @ViewChild('companyForm')
  public companyForm: NgForm;

  public companyGroups: any[];
  public commercialConditions: any[];

  public companyData = {
    id: null,
    name: null,
    address: null,
    zip: null,
    cif: null,
    web: null,
    phone: null,
    group: null,
    groupId: null,
    idNavision: null,
    commercialCondition: null,
    office_city: null,
    country: null,
    offices: null
  };

  public selectedCountry: any;
  public cityPlaceholder = 'Utebo';

  private originalCompanyData: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private miscService: MiscService,
    private errorBox: ErrorBoxService,
    private cityService: CityService
  ) {
    this.getCompanyGroups();
    this.getCommercialConditions();
  }

  ngOnInit() {
    // Observar cambios en companyToEdit para cargar datos cuando se abre el offcanvas
    if (this.companyToEdit) {
      this.loadCompanyData(this.companyToEdit);
    }
  }

  ngOnChanges() {
    if (this.companyToEdit && this.isOffcanvasOpen) {
      this.loadCompanyData(this.companyToEdit);
    }
  }

  loadCompanyData(company: Company) {
    this.company = company;
    this.companyData = {...company};

    // Cargar datos de ubicación desde la oficina predeterminada si existe
    if (company.offices && company.offices.length > 0) {
      const defaultOffice = company.offices.find(office => office.isDefault) || company.offices[0];
      if (defaultOffice) {
        this.companyData.office_city = defaultOffice.city;
        if (defaultOffice.city && defaultOffice.city.country) {
          this.selectedCountry = defaultOffice.city.country;
          this.companyData.country = defaultOffice.city.country;
        }
        if (defaultOffice.zipCode) {
          this.companyData.zip = defaultOffice.zipCode;
        }
      }
    }

    // Manejar condiciones comerciales del backend
    if (this.companyData.commercialCondition) {
      // Si viene el objeto completo del backend, extraer el ID
      if (typeof this.companyData.commercialCondition === 'object') {
        this.companyData.commercialCondition = this.companyData.commercialCondition.id;
      }
    } else {
      this.companyData.commercialCondition = '';
    }

    // Sincronizar groupId
    this.syncGroupId();



    this.originalCompanyData = {...this.companyData};
    this.isEditMode = false; // Iniciar en modo solo lectura
  }

  getCompanyGroups(): any {
    this.miscService.getCompanyGroups()
      .then(companyGroups => {
        this.companyGroups = companyGroups;
        // Sincronizar groupId después de cargar los grupos
        this.syncGroupId();
      });
  }

  getCommercialConditions(): any {
    this.miscService.getCommercialConditions()
      .then(commercialConditions => {
        this.commercialConditions = commercialConditions;
      })
      .catch(error => {
        console.error('Error loading commercial conditions:', error);
        this.commercialConditions = [];
      });
  }

  byId(item1, item2) {
    if (!item1 || !item2) {
      return false;
    }
    return item1.id === item2.id;
  }

  onSubmit() {
    this.updatingCompany = true;

    // Preparar datos para envío
    let updateData = {...this.companyData};


    // Eliminar country si es objeto
    if (typeof updateData.country === 'object') {
      delete updateData.country;
    }

    // Convertir groupId de vuelta al objeto group
    if (updateData.groupId && this.companyGroups) {
      const selectedGroup = this.companyGroups.find(group => group.id == updateData.groupId);
      if (selectedGroup) {
        updateData.group = selectedGroup;
      }
    }
    // Eliminar groupId ya que no se envía al backend
    delete updateData.groupId;

    // commercialCondition ya está en el formato correcto para el backend


    this.companyService.updateCompany(updateData)
      .then(company => {
        this.updatingCompany = false;
        this.errorBox.success('Company updated successfully.');
        this.companyUpdated.emit(company);
        this.closeOffcanvas();
      })
      .catch(error => {
        this.updatingCompany = false;
        this.errorBox.error('Error updating company.');
        console.error('Error updating company:', error);
      });
  }

  compareData(): boolean {
    return _.isEqual(this.originalCompanyData, this.companyData);
  }

  closeOffcanvas() {
    this.isEditMode = false; // Resetear modo al cerrar
    this.offcanvasClose.emit();
  }

  openOffcanvas() {
    this.isOffcanvasOpen = true;
  }

  isViewMode(): boolean {
    return !this.isEditMode;
  }

  resetForm() {
    if (this.originalCompanyData) {
      this.companyData = {...this.originalCompanyData};
    }
    this.closeOffcanvas();
  }

  // Funciones de búsqueda para país y ciudad
  searchCountry = (text$: Observable<string>) =>
    text$
      .debounceTime(300)
      .distinctUntilChanged()
      .switchMap(term => {
        return term.length <= 2 ? [] : this.cityService.searchCountries(term)
          .catch(() => Observable.of([]));
      })

  formatterCountry = (x: {name: string}) => x.name;

  searchSite = (text$: Observable<string>) =>
    text$
      .debounceTime(300)
      .distinctUntilChanged()
      .switchMap(term => {
        if (!this.companyData.country || !this.companyData.country.id) {
          return Observable.of([]);
        }
        return Observable.fromPromise(this.cityService.getCitiesByCountry(this.companyData.country.id, term));
      })
      .catch(() => Observable.of([]));

  formatterSiteOffice = (x: { nameUtf8: string, state: { nameUtf8: string } }) => {
    return x.nameUtf8 + (x.state ? ', ' + x.state.nameUtf8 : '')
  };

  selectedCountryTrigger($country) {
    this.cityPlaceholder = $country.item.capital;
    // Limpiar ciudad cuando cambia el país
    this.companyData.office_city = null;
  }

  getCommercialConditionDisplay(): string {
    if (!this.companyData.commercialCondition || !this.commercialConditions) {
      return 'No especificado';
    }

    const condition = this.commercialConditions.find(c => c.id == this.companyData.commercialCondition);
    return condition ? `Type ${condition.type}` : 'No especificado';
  }

  getCompanyGroupDisplay(): string {
    if (!this.companyData.group || !this.companyData.group.name || !this.companyData.group.description) {
      return 'No especificado';
    }

    return `${this.companyData.group.name} - ${this.companyData.group.description}`;
  }

  syncGroupId() {
    if (this.companyData.group && this.companyGroups) {
      const foundGroup = this.companyGroups.find(g => g.name === this.companyData.group.name);
      if (foundGroup) {
        this.companyData.groupId = foundGroup.id;
      } else {
        this.companyData.groupId = '';
      }
    }
  }


}
